/**
 * API client for the experimental lrscribe backend.
 *
 * All calls go through the same-origin Express proxy at /api/experimental-proxy/*
 * to avoid CORS issues. The proxy forwards the Authorization header to the new
 * backend at EXPERIMENTAL_BACKEND_URL (server-side env var).
 *
 * Two-token discipline (per the integration guide):
 *   - OIDC id token: used for every route except health and chunk uploads.
 *   - Upload token: returned by mintSession; rotates on every successful chunk.
 *     Must NOT be mixed up with the id token.
 */

const PROXY_BASE = "/api/experimental-proxy";
const REFRESH_BUFFER_MS = 60_000;

let idTokenCache: { idToken: string; expiresAt: number } | null = null;

// ---------------------------------------------------------------------------
// Diagnostics log
// ---------------------------------------------------------------------------

export interface DiagnosticsEntry {
  id: number;
  timestamp: string;
  method: string;
  path: string;
  httpStatus: number | null;
  tokenType: "id-token" | "upload-token" | "none";
  responseBodyText: string | null;
  error?: string;
}

/**
 * Keys whose values contain bearer credentials and must never appear in the log.
 * Applied recursively to parsed JSON bodies before storing.
 */
const SENSITIVE_KEYS = new Set([
  "uploadtoken",
  "token",
  "idtoken",
  "accesstoken",
  "refreshtoken",
  "authorization",
  "bearer",
  "secret",
  "password",
  "credential",
]);

function redactObject(obj: unknown): unknown {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(redactObject);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (SENSITIVE_KEYS.has(k.toLowerCase())) {
      out[k] = "[REDACTED]";
    } else {
      out[k] = redactObject(v);
    }
  }
  return out;
}

/**
 * Redact sensitive fields from a response body text before storing it.
 * If the text is JSON, parse → redact → re-stringify. Otherwise return as-is.
 * Always safe to call — never throws.
 */
function redactBodyText(text: string | null): string | null {
  if (!text) return text;
  try {
    const parsed = JSON.parse(text);
    return JSON.stringify(redactObject(parsed));
  } catch {
    return text;
  }
}

let diagLog: DiagnosticsEntry[] = [];
let diagSeq = 0;
const diagListeners = new Set<() => void>();

function addDiagEntry(entry: Omit<DiagnosticsEntry, "id" | "timestamp">): void {
  const newEntry: DiagnosticsEntry = {
    id: ++diagSeq,
    timestamp: new Date().toISOString(),
    ...entry,
    responseBodyText: redactBodyText(entry.responseBodyText),
  };
  diagLog = [...diagLog.slice(-99), newEntry];
  diagListeners.forEach((fn) => fn());
}

export function getDiagnosticsLog(): DiagnosticsEntry[] {
  return diagLog;
}

export function clearDiagnosticsLog(): void {
  diagLog = [];
  diagListeners.forEach((fn) => fn());
}

export function subscribeDiagnostics(fn: () => void): () => void {
  diagListeners.add(fn);
  return () => diagListeners.delete(fn);
}

// ---------------------------------------------------------------------------
// Upload-token rotation state
// ---------------------------------------------------------------------------

export interface UploadTokenRotationState {
  count: number;
  lastRotatedAt: string | null;
}

let uploadTokenRotationState: UploadTokenRotationState = { count: 0, lastRotatedAt: null };
const rotationListeners = new Set<() => void>();

function bumpRotationCounter(): void {
  uploadTokenRotationState = {
    count: uploadTokenRotationState.count + 1,
    lastRotatedAt: new Date().toISOString(),
  };
  rotationListeners.forEach((fn) => fn());
}

export function resetUploadTokenRotationState(): void {
  uploadTokenRotationState = { count: 0, lastRotatedAt: null };
  rotationListeners.forEach((fn) => fn());
}

export function getUploadTokenRotationState(): UploadTokenRotationState {
  return uploadTokenRotationState;
}

export function subscribeUploadTokenRotation(fn: () => void): () => void {
  rotationListeners.add(fn);
  return () => rotationListeners.delete(fn);
}

// ---------------------------------------------------------------------------
// Id-token claim decoding (client-side, no raw token exposed)
// ---------------------------------------------------------------------------

export interface IdTokenClaims {
  iss: string | null;
  aud: string | string[] | null;
  hasSub: boolean;
  exp: number | null;
  expHuman: string | null;
  secondsUntilExpiry: number | null;
  decodeError?: string;
}

function base64urlDecode(str: string): string {
  const base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "===".slice((base64.length + 3) % 4);
  return atob(padded);
}

export function decodeIdTokenClaims(idToken: string): IdTokenClaims {
  try {
    const parts = idToken.split(".");
    if (parts.length < 2) throw new Error("Not a valid JWT (expected 3 parts)");
    const payload = JSON.parse(base64urlDecode(parts[1]));
    const now = Math.floor(Date.now() / 1000);
    const exp = typeof payload.exp === "number" ? payload.exp : null;
    return {
      iss: typeof payload.iss === "string" ? payload.iss : null,
      aud: payload.aud ?? null,
      hasSub: !!payload.sub,
      exp,
      expHuman: exp ? new Date(exp * 1000).toLocaleString() : null,
      secondsUntilExpiry: exp !== null ? exp - now : null,
    };
  } catch (e: any) {
    return {
      iss: null,
      aud: null,
      hasSub: false,
      exp: null,
      expHuman: null,
      secondsUntilExpiry: null,
      decodeError: e?.message ?? "Unknown decode error",
    };
  }
}

export function getDecodedCurrentIdTokenClaims(): IdTokenClaims | null {
  if (!idTokenCache) return null;
  return decodeIdTokenClaims(idTokenCache.idToken);
}

export function subscribeIdTokenClaims(fn: () => void): () => void {
  diagListeners.add(fn);
  return () => diagListeners.delete(fn);
}

// ---------------------------------------------------------------------------
// Core token fetch
// ---------------------------------------------------------------------------

export async function getExperimentalIdToken(): Promise<string> {
  const now = Date.now();
  if (idTokenCache && idTokenCache.expiresAt * 1000 - now > REFRESH_BUFFER_MS) {
    return idTokenCache.idToken;
  }
  const res = await fetch("/api/auth/experimental-id-token", {
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(
      new Error(body?.message || `Failed to get id token: ${res.status}`),
      { status: res.status, stage: "id-token" },
    );
  }
  const data: { idToken: string; expiresAt: number } = await res.json();
  idTokenCache = data;
  rotationListeners.forEach((fn) => fn());
  return data.idToken;
}

export function invalidateExperimentalIdTokenCache(): void {
  idTokenCache = null;
}

// ---------------------------------------------------------------------------
// Authenticated fetch (id token)
// ---------------------------------------------------------------------------

async function expFetch(
  path: string,
  init: RequestInit & { idToken?: string } = {},
): Promise<Response> {
  const idToken = init.idToken ?? (await getExperimentalIdToken());
  const headers: Record<string, string> = {
    Authorization: `Bearer ${idToken}`,
    ...(init.headers as Record<string, string>),
  };
  const url = `${PROXY_BASE}${path}`;

  let res: Response;
  try {
    res = await fetch(url, { ...init, headers });
  } catch (err: any) {
    addDiagEntry({
      method: (init.method ?? "GET").toUpperCase(),
      path,
      httpStatus: null,
      tokenType: "id-token",
      responseBodyText: null,
      error: err?.message ?? "Network error",
    });
    throw err;
  }

  const bodyText = await res.clone().text().catch(() => null);

  if (res.status === 401 && !init.idToken) {
    addDiagEntry({
      method: (init.method ?? "GET").toUpperCase(),
      path,
      httpStatus: res.status,
      tokenType: "id-token",
      responseBodyText: bodyText,
    });
    invalidateExperimentalIdTokenCache();
    const freshToken = await getExperimentalIdToken();
    const retryHeaders: Record<string, string> = {
      Authorization: `Bearer ${freshToken}`,
      ...(init.headers as Record<string, string>),
    };
    let retryRes: Response;
    try {
      retryRes = await fetch(url, { ...init, headers: retryHeaders });
    } catch (err: any) {
      addDiagEntry({
        method: (init.method ?? "GET").toUpperCase(),
        path,
        httpStatus: null,
        tokenType: "id-token",
        responseBodyText: null,
        error: `Retry network error: ${err?.message ?? "Unknown"}`,
      });
      throw err;
    }
    const retryBody = await retryRes.clone().text().catch(() => null);
    addDiagEntry({
      method: (init.method ?? "GET").toUpperCase(),
      path,
      httpStatus: retryRes.status,
      tokenType: "id-token",
      responseBodyText: retryBody,
      error: "(retry after 401)",
    });
    return retryRes;
  }

  addDiagEntry({
    method: (init.method ?? "GET").toUpperCase(),
    path,
    httpStatus: res.status,
    tokenType: "id-token",
    responseBodyText: bodyText,
  });

  return res;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export type HealthResult =
  | { ok: true; status: string; checkedAt: string }
  | { ok: false; reason: string };

export async function checkHealth(): Promise<HealthResult> {
  try {
    const res = await fetch(`${PROXY_BASE}/health`, { signal: AbortSignal.timeout(6000) });
    const bodyText = await res.clone().text().catch(() => null);
    addDiagEntry({
      method: "GET",
      path: "/health",
      httpStatus: res.status,
      tokenType: "none",
      responseBodyText: bodyText,
    });
    const body = await res.json().catch(() => ({}));
    if (res.ok) {
      return { ok: true, status: body.status ?? "healthy", checkedAt: body.checkedAt ?? "" };
    }
    return { ok: false, reason: body.error ?? `HTTP ${res.status}` };
  } catch (err: any) {
    const isTimeout = err?.name === "TimeoutError" || err?.name === "AbortError";
    addDiagEntry({
      method: "GET",
      path: "/health",
      httpStatus: null,
      tokenType: "none",
      responseBodyText: null,
      error: isTimeout ? "timeout" : (err?.message ?? "unreachable"),
    });
    return { ok: false, reason: isTimeout ? "timeout" : "unreachable" };
  }
}

export interface ExperimentalConfig {
  oidcConfigured: boolean;
  oidcIssuer: string | null;
  oidcAudience: string | null;
  fetchError?: { status: number; message: string };
}

export async function getExperimentalConfig(): Promise<ExperimentalConfig> {
  let res: Response;
  try {
    res = await fetch("/api/experimental/config", {
      credentials: "include",
      signal: AbortSignal.timeout(8000),
    });
  } catch (err: any) {
    const isTimeout = err?.name === "TimeoutError" || err?.name === "AbortError";
    return {
      oidcConfigured: false,
      oidcIssuer: null,
      oidcAudience: null,
      fetchError: {
        status: 0,
        message: isTimeout ? "Request timed out" : (err?.message ?? "Network error"),
      },
    };
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    return {
      oidcConfigured: false,
      oidcIssuer: null,
      oidcAudience: null,
      fetchError: {
        status: res.status,
        message: body?.message ?? body?.error ?? `HTTP ${res.status}`,
      },
    };
  }
  return res.json();
}

export interface Team {
  id: string;
  name: string;
  isPersonal: boolean;
  status: string;
}

export async function listTeams(): Promise<Team[]> {
  const res = await expFetch("/api/teams", { method: "GET" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(
      new Error(body?.error ?? `listTeams failed: ${res.status}`),
      { status: res.status, stage: "list-teams" },
    );
  }
  return res.json();
}

export async function createTeam(name: string): Promise<Team> {
  const res = await expFetch("/api/teams", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(
      new Error(body?.error ?? `createTeam failed: ${res.status}`),
      { status: res.status, stage: "create-team" },
    );
  }
  return res.json();
}

/**
 * Branded ID types — the backend has two separate ID namespaces:
 *   - UploadSessionId: minted by POST /sessions, used only for audio chunk uploads.
 *   - RecordingId: created by POST /recordings, used only for document generation.
 * Branding them prevents accidentally passing one where the other is expected.
 */
export type UploadSessionId = string & { readonly __brand: "UploadSessionId" };
export type RecordingId = string & { readonly __brand: "RecordingId" };

export interface CreatedRecording {
  id: RecordingId;
  title: string;
}

export interface CreateRecordingParams {
  title: string;
  description: string;
  notes: string;
}

export async function createRecording(
  teamId: string,
  params: CreateRecordingParams,
): Promise<CreatedRecording> {
  const res = await expFetch(`/api/teams/${teamId}/recordings`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(
      new Error(body?.error ?? `createRecording failed: ${res.status}`),
      { status: res.status, stage: "create-recording", errorCode: body?.error },
    );
  }
  return res.json();
}

export interface MintedSession {
  session: UploadSessionId;
  uploadToken: string;
  expiresAt: string;
}

export async function mintSession(teamId: string): Promise<MintedSession> {
  const res = await expFetch(`/api/teams/${teamId}/sessions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(
      new Error(body?.error ?? `mintSession failed: ${res.status}`),
      { status: res.status, stage: "mint-session" },
    );
  }
  return res.json();
}

/**
 * Upload one audio chunk using the rotating upload token (NOT the id token).
 * Returns the next upload token to use.
 * Chunk uploads use the upload token, never the id token.
 */
export async function uploadChunk(
  teamId: string,
  sessionId: UploadSessionId,
  uploadToken: string,
  blob: Blob,
): Promise<string> {
  const mimeType = blob.type || "audio/webm";
  const path = `/api/teams/${teamId}/sessions/${sessionId}/audio`;
  const url = `${PROXY_BASE}${path}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${uploadToken}`,
        "Content-Type": mimeType,
      },
      body: blob,
    });
  } catch (err: any) {
    addDiagEntry({
      method: "POST",
      path,
      httpStatus: null,
      tokenType: "upload-token",
      responseBodyText: null,
      error: err?.message ?? "Chunk upload network error",
    });
    throw Object.assign(new Error("Chunk upload network error"), {
      stage: "chunk-network",
      retryable: true,
    });
  }

  const bodyText = await res.clone().text().catch(() => null);
  addDiagEntry({
    method: "POST",
    path,
    httpStatus: res.status,
    tokenType: "upload-token",
    responseBodyText: bodyText,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = body?.error ?? `Chunk upload failed: ${res.status}`;
    const isTransient5xx = res.status >= 500 && res.status < 600;
    throw Object.assign(new Error(error), {
      status: res.status,
      stage: "chunk-http",
      errorCode: error,
      retryable: isTransient5xx,
    });
  }

  const data = await res.json();
  bumpRotationCounter();
  return data.uploadToken as string;
}

export async function generateDocument(
  teamId: string,
  recordingId: RecordingId,
  body: {
    sectionContents: string;
    examples: string[];
    formattingInstructions: string;
  },
): Promise<string> {
  const res = await expFetch(
    `/api/teams/${teamId}/recordings/${recordingId}/document`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    const responseBody = await res.json().catch(() => ({}));
    throw Object.assign(
      new Error(responseBody?.error ?? `Document generation failed: ${res.status}`),
      { status: res.status, stage: "generate-document" },
    );
  }
  const data = await res.json();
  return data.document as string;
}
